import { useState } from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Info,
  Loader2,
  Plus,
  Search,
  X,
} from 'lucide-react';
import { pointsForPlacement } from '@fq/common/src/pickem-placement';
import type { PickemCompetitor } from '@api/generated/types.gen';
import { personalBracket, startingMatches } from '@fq/pickem-ui';
import { PickemBracket } from './pickem-bracket';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CompetitorProfile } from './competitor-profile';
import type {
  PlayerPoolProps,
  PlayerAvatarProps,
  PlayerTarget,
} from './player-pool.types';

function PlayerAvatar({ competitor: player }: PlayerAvatarProps) {
  return (
    <Avatar className="h-10 w-10 shrink-0">
      <AvatarImage
        src={
          player.imageUrl ||
          player.startggProfile?.participants[0]?.avatarUrl ||
          undefined
        }
        alt=""
      />
      <AvatarFallback>{player.name.slice(0, 2).toUpperCase()}</AvatarFallback>
    </Avatar>
  );
}

export function PlayerPool({
  matches,
  format,
  bracketLayout,
  userPicks,
  competitors,
  lineup,
  disabled,
  saving,
  disabledReason,
  onChange,
}: PlayerPoolProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('seed');
  const [visibleCount, setVisibleCount] = useState(30);
  const [target, setTarget] = useState<PlayerTarget | null>(null);
  const [profile, setProfile] = useState<PickemCompetitor | null>(null);
  const [availableOnly, setAvailableOnly] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [view, setView] = useState<'players' | 'bracket'>('players');
  const roots = startingMatches(matches, format);
  const slots = roots.flatMap((match, i) =>
    (['top', 'bottom'] as const).map((slot, j) => ({
      bracketMatchId: match.id,
      slot,
      number: i * 2 + j + 1,
      label: `Pick ${i * 2 + j + 1} · Match ${i + 1}${format === 'double_elimination_seeded' ? (match.section === 'lower' ? ' · Losers bracket' : ' · Winners bracket') : ''}`,
    })),
  );
  const byId = new Map(competitors.map((c) => [c.id, c]));
  const chosen = new Set(lineup.map((s) => s.competitorId));
  const free = slots.find(
    (slot) =>
      !lineup.some(
        (s) => s.bracketMatchId === slot.bracketMatchId && s.slot === slot.slot,
      ),
  );
  const active = target ?? free;
  const activeSlot = slots.find(
    (s) =>
      s.bracketMatchId === active?.bracketMatchId && s.slot === active?.slot,
  );
  const activeLabel = activeSlot?.label;
  const activeEntry = lineup.find(
    (s) =>
      s.bracketMatchId === active?.bracketMatchId && s.slot === active?.slot,
  );
  const replacing = activeEntry
    ? byId.get(activeEntry.competitorId)
    : undefined;
  const selectedCount = slots.filter((slot) =>
    lineup.some(
      (s) => s.bracketMatchId === slot.bracketMatchId && s.slot === slot.slot,
    ),
  ).length;
  const remaining = slots.length - selectedCount;
  const complete = slots.length > 0 && remaining === 0;
  const readOnly = disabled && !saving;
  const openPool = (slot: PlayerTarget | null = null) => {
    setTarget(slot);
    setSearch('');
    setVisibleCount(30);
    setFeedback('');
    setView('players');
    setOpen(true);
  };
  const query = search.trim().toLocaleLowerCase();
  const filtered = competitors
    .filter((c) => !availableOnly || (!chosen.has(c.id) && !c.withdrawn))
    .filter((c) =>
      [
        c.name,
        ...(c.startggProfile?.participants.flatMap((p) => [
          p.gamerTag,
          p.prefix,
          p.country,
        ]) ?? []),
      ].some((value) => value?.toLocaleLowerCase().includes(query)),
    )
    .sort((a, b) =>
      sort === 'name'
        ? a.name.localeCompare(b.name)
        : (a.startggProfile?.seed ?? (a.seed || Infinity)) -
            (b.startggProfile?.seed ?? (b.seed || Infinity)) ||
          a.name.localeCompare(b.name),
    );
  const selectPlayer = (player: PickemCompetitor) => {
    if (disabled || !active || chosen.has(player.id) || player.withdrawn)
      return;
    onChange([
      ...lineup.filter(
        (s) =>
          s.bracketMatchId !== active.bracketMatchId || s.slot !== active.slot,
      ),
      {
        bracketMatchId: active.bracketMatchId,
        slot: active.slot,
        competitorId: player.id,
      },
    ]);
    setTarget(active);
    setSearch('');
    setVisibleCount(30);
    setFeedback(
      replacing
        ? `${player.name} replaced ${replacing.name}.`
        : `${player.name} added to your lineup.`,
    );
  };
  return (
    <section
      className="rounded-xl border border-border bg-card p-4 space-y-4"
      aria-label="Your tournament lineup"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs text-primary font-semibold uppercase tracking-wide">
            Step 1 · Choose your players
          </p>
          <h2 className="text-lg font-semibold">
            {slots.length > 0
              ? `Build your top ${slots.length}`
              : 'Explore the players'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {slots.length > 0
              ? `Pick the players you think will make the top ${slots.length}. Then predict each match winner in your bracket.`
              : 'Browse player profiles while the organizer prepares the bracket.'}
          </p>
          <p className="text-sm text-muted-foreground mt-2" aria-live="polite">
            {selectedCount} / {slots.length} selected
            {complete
              ? ' · Ready to pick winners'
              : remaining > 0
                ? ` · Choose ${remaining} more`
                : ''}
          </p>
        </div>
        <Button className="min-h-11" onClick={() => openPool()}>
          {readOnly
            ? 'Browse players'
            : complete
              ? 'Edit lineup'
              : 'Choose players'}
        </Button>
      </div>
      {slots.length > 0 && (
        <div
          role="progressbar"
          aria-label="Players selected"
          aria-valuemin={0}
          aria-valuemax={slots.length}
          aria-valuenow={selectedCount}
          className="h-1.5 overflow-hidden rounded-full bg-muted"
        >
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${(selectedCount / slots.length) * 100}%` }}
          />
        </div>
      )}
      {slots.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Players with the same match number face each other in your prediction.
          Pick numbers match the labels in your bracket; they aren't seeds or
          finishing places.
        </p>
      )}
      {slots.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          The organizer is preparing the bracket. You can browse the roster now.
        </p>
      ) : (
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 lg:grid-cols-4 gap-2">
          {slots.map((slot) => {
            const entry = lineup.find(
              (s) =>
                s.bracketMatchId === slot.bracketMatchId &&
                s.slot === slot.slot,
            );
            const player = entry ? byId.get(entry.competitorId) : undefined;
            return (
              <div
                key={slot.bracketMatchId + slot.slot}
                className={`group h-[98px] rounded-lg border p-3 space-y-2 min-w-0 transition-colors duration-150 ${!saving && (!readOnly || player) ? 'hover:border-primary hover:bg-primary/5 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30' : ''}`}
              >
                <div className="flex h-6 items-center justify-between gap-1">
                  <p
                    className="truncate text-xs text-muted-foreground"
                    title={slot.label}
                  >
                    {slot.label}
                  </p>
                  {player && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        aria-label={`View ${player.name}'s profile`}
                        title="Player profile"
                        onClick={() => setProfile(player)}
                      >
                        <Info className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={disabled}
                        aria-label={`Remove ${player.name} from pick ${slot.number}`}
                        title="Remove player"
                        onClick={() =>
                          onChange(
                            lineup.filter(
                              (s) =>
                                s.bracketMatchId !== slot.bracketMatchId ||
                                s.slot !== slot.slot,
                            ),
                          )
                        }
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="flex h-10 w-full items-center gap-2 rounded-md text-left transition-colors enabled:cursor-pointer enabled:hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card disabled:cursor-not-allowed"
                  title={
                    player
                      ? `${readOnly ? 'View' : 'Change'} ${player.name}`
                      : `Pick as #${slot.number}`
                  }
                  disabled={saving || (!player && readOnly)}
                  aria-label={
                    player
                      ? `${readOnly ? 'View' : 'Change'} ${player.name}, ${slot.label}`
                      : `Choose a player for ${slot.label}`
                  }
                  onClick={() => {
                    if (player && readOnly) setProfile(player);
                    else {
                      openPool(slot);
                    }
                  }}
                >
                  {player ? (
                    <>
                      <PlayerAvatar competitor={player} />
                      <span className="text-sm font-medium min-w-0">
                        <span className="block truncate">{player.name}</span>
                        {!player.withdrawn &&
                          player.placementFinal &&
                          player.finalPlacement != null && (
                            <span className="block truncate text-xs text-muted-foreground">
                              Finished {player.finalPlacement} · +
                              {pointsForPlacement(player.finalPlacement)}{' '}
                              placement points
                            </span>
                          )}
                        {player.withdrawn && (
                          <span className="block truncate text-xs text-destructive">
                            {readOnly
                              ? 'Withdrawn'
                              : 'Withdrawn · choose a replacement'}
                          </span>
                        )}
                      </span>
                    </>
                  ) : (
                    <span
                      className={`text-sm text-muted-foreground transition-colors ${!disabled ? 'group-hover:text-primary group-focus-within:text-primary' : ''}`}
                    >
                      <Plus
                        className="mr-1 inline h-4 w-4"
                        aria-hidden="true"
                      />{' '}
                      Pick as #{slot.number}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <p aria-live="polite" className="text-xs text-muted-foreground">
        {saving
          ? 'Saving your lineup…'
          : readOnly
            ? (disabledReason ??
              'Player selection is unavailable. You can still browse profiles.')
            : complete
              ? 'Your lineup is ready. Next, pick the winners in the bracket below.'
              : 'Saved automatically. You can change your players until picks close.'}
      </p>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-3xl max-h-[95dvh] flex flex-col gap-3 overflow-hidden p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {readOnly || slots.length === 0
                ? 'Tournament players'
                : replacing
                  ? `Replace ${replacing.name}`
                  : complete
                    ? 'Your lineup is ready'
                    : `Choose your top ${slots.length}`}
            </DialogTitle>
            <DialogDescription>
              {readOnly
                ? (disabledReason ??
                  'Player selection is unavailable. You can still browse profiles.')
                : replacing
                  ? 'Choose a new player for this matchup. Winner picks affected by this change will be cleared.'
                  : slots.length === 0
                    ? 'Browse the roster while the organizer prepares the bracket.'
                    : complete
                      ? 'Tap a name in your lineup to replace that player, or continue to your bracket.'
                      : 'Add players to your lineup. Each selection saves automatically.'}
            </DialogDescription>
          </DialogHeader>
          {slots.length > 0 && (
            <div className="shrink-0 rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="font-medium">
                  Your lineup{' '}
                  <span className="text-muted-foreground">
                    {selectedCount}/{slots.length}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {complete
                    ? 'All players chosen'
                    : `${remaining} more to choose`}
                </span>
              </div>
              <div
                role="progressbar"
                aria-label="Lineup completion"
                aria-valuemin={0}
                aria-valuemax={slots.length}
                aria-valuenow={selectedCount}
                aria-valuetext={`${selectedCount} of ${slots.length} players selected`}
                className="flex h-1.5 gap-1.5"
              >
                {slots.map((slot) => {
                  const filled = lineup.some(
                    (entry) =>
                      entry.bracketMatchId === slot.bracketMatchId &&
                      entry.slot === slot.slot,
                  );
                  return (
                    <span
                      key={slot.bracketMatchId + slot.slot}
                      className={`h-full flex-1 rounded-full transition-colors duration-200 ${filled ? 'bg-primary' : 'bg-primary/15'}`}
                    />
                  );
                })}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {slots.map((slot, index) => {
                  const entry = lineup.find(
                    (s) =>
                      s.bracketMatchId === slot.bracketMatchId &&
                      s.slot === slot.slot,
                  );
                  const player = entry
                    ? byId.get(entry.competitorId)
                    : undefined;
                  const isActive =
                    active?.bracketMatchId === slot.bracketMatchId &&
                    active?.slot === slot.slot;
                  return (
                    <button
                      key={slot.bracketMatchId + slot.slot}
                      type="button"
                      disabled={disabled}
                      aria-pressed={isActive}
                      aria-label={`${slot.label}: ${player?.name ?? 'empty'}${player ? '. Choose to replace' : ''}`}
                      className={`shrink-0 rounded-md border px-3 py-2 text-left text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default ${isActive && !readOnly ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'}`}
                      onClick={() => {
                        setTarget(slot);
                        setFeedback('');
                        setView('players');
                      }}
                    >
                      <span className="block text-muted-foreground">
                        Pick {index + 1} · Match {Math.floor(index / 2) + 1}
                      </span>
                      <span className="block max-w-36 truncate font-medium mt-1">
                        {player?.name ?? `Pick as #${slot.number}`}
                      </span>
                    </button>
                  );
                })}
              </div>
              {!readOnly && activeLabel && (
                <p className="text-xs text-muted-foreground">
                  {replacing
                    ? `Replacing ${replacing.name}`
                    : 'Adding a player'}{' '}
                  · {activeLabel}
                </p>
              )}
            </div>
          )}
          {slots.length > 0 && (
            <fieldset
              className="flex shrink-0 gap-2"
              aria-label="Player picker view"
            >
              <Button
                size="sm"
                variant={view === 'players' ? 'default' : 'outline'}
                aria-pressed={view === 'players'}
                onClick={() => setView('players')}
              >
                Players
              </Button>
              <Button
                size="sm"
                variant={view === 'bracket' ? 'default' : 'outline'}
                aria-pressed={view === 'bracket'}
                onClick={() => setView('bracket')}
              >
                Bracket preview
              </Button>
            </fieldset>
          )}
          {view === 'players' && (
            <>
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <Search
                    className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    aria-label="Search players, sponsors or countries"
                    placeholder="Search name, sponsor, country…"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setVisibleCount(30);
                    }}
                    className="min-h-11 pl-9 pr-11"
                  />
                  {search && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-11 w-11"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearch('');
                        setVisibleCount(30);
                      }}
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger
                    aria-label="Sort players"
                    className="sm:w-44 min-h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seed">Tournament seed</SelectItem>
                    <SelectItem value="name">Name A–Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {filtered.length}{' '}
                  {filtered.length === 1 ? 'player' : 'players'}
                  {query ? ' found' : ' in the pool'}
                </p>
                <Button
                  variant={availableOnly ? 'secondary' : 'ghost'}
                  size="sm"
                  aria-pressed={availableOnly}
                  onClick={() => {
                    setAvailableOnly((value) => !value);
                    setVisibleCount(30);
                  }}
                >
                  Available only
                </Button>
              </div>
            </>
          )}
          <div
            key={view}
            className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-1"
          >
            {view === 'bracket' ? (
              <section
                className="space-y-3"
                aria-label="Your numbered bracket preview"
              >
                <p className="text-xs text-muted-foreground">
                  Each number matches a pick in your lineup above. Choose a
                  lineup pick to add or change its player. These are your
                  predicted matchups, not the official draw.
                </p>
                <PickemBracket
                  matches={personalBracket(matches, lineup, format).map(
                    (match) => ({
                      ...match,
                      status: 'pending' as const,
                      actualLoserScore: null,
                      actualReset: null,
                      scoreTop: null,
                      scoreBottom: null,
                    }),
                  )}
                  competitors={competitors}
                  userPicks={userPicks}
                  bracketFormat={format}
                  bracketLayout={bracketLayout}
                  showLineupNumbers
                />
              </section>
            ) : (
              <>
                {filtered.slice(0, visibleCount).map((player) => {
                  const p = player.startggProfile?.participants[0];
                  return (
                    <div
                      key={player.id}
                      className={`rounded-lg border p-3 ${chosen.has(player.id) ? 'border-primary bg-primary/5' : ''}`}
                    >
                      <div className="flex flex-wrap items-center gap-3">
                        <PlayerAvatar competitor={player} />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium break-words">
                            {player.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {[
                              p?.prefix,
                              p?.country,
                              (player.startggProfile?.seed ?? player.seed)
                                ? `Seed #${player.startggProfile?.seed ?? player.seed}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <div className="flex w-full gap-2 sm:w-auto">
                          <Button
                            variant="ghost"
                            className="min-h-11 flex-1 sm:flex-none"
                            onClick={() => setProfile(player)}
                          >
                            Profile
                          </Button>
                          <Button
                            variant={
                              chosen.has(player.id) ? 'secondary' : 'default'
                            }
                            className="min-h-11 flex-1 sm:flex-none sm:min-w-28"
                            aria-label={
                              chosen.has(player.id)
                                ? `${player.name} is in your lineup`
                                : `Pick ${player.name} as #${activeSlot?.number ?? 'unselected'}`
                            }
                            disabled={
                              disabled ||
                              chosen.has(player.id) ||
                              player.withdrawn ||
                              !active
                            }
                            onClick={() => selectPlayer(player)}
                          >
                            {player.withdrawn ? (
                              'Withdrawn'
                            ) : chosen.has(player.id) ? (
                              <>
                                <Check
                                  className="mr-1 h-4 w-4"
                                  aria-hidden="true"
                                />{' '}
                                Selected
                              </>
                            ) : activeSlot ? (
                              `Pick as #${activeSlot.number}`
                            ) : (
                              'Choose a pick'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!filtered.length && (
                  <div className="py-8 text-center space-y-2">
                    <p className="font-medium">
                      {query
                        ? `No players found for “${search.trim()}”`
                        : 'No available players'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {query
                        ? 'Try a player name, sponsor or country.'
                        : 'Show all players to review your selections and withdrawn players.'}
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearch('');
                        setAvailableOnly(false);
                        setVisibleCount(30);
                      }}
                    >
                      Reset filters
                    </Button>
                  </div>
                )}
                {filtered.length > visibleCount && (
                  <Button
                    variant="outline"
                    className="w-full min-h-11"
                    onClick={() => setVisibleCount((n) => n + 30)}
                  >
                    Show more ({filtered.length - visibleCount} remaining)
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="shrink-0 border-t pt-3 space-y-3">
            <output className="flex items-center gap-2 text-xs text-muted-foreground min-h-4">
              {saving ? (
                <>
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />{' '}
                  Saving your lineup…
                </>
              ) : feedback ? (
                <>
                  <CheckCircle2
                    className="h-4 w-4 text-primary"
                    aria-hidden="true"
                  />{' '}
                  {feedback}
                </>
              ) : readOnly || slots.length === 0 ? (
                'Browse profiles to learn more about the players.'
              ) : complete ? (
                'Ready for step 2: predict each match winner.'
              ) : (
                'You can return anytime to finish your lineup.'
              )}
            </output>
            <Button
              variant={complete && !readOnly ? 'default' : 'outline'}
              className="w-full min-h-11"
              onClick={() => setOpen(false)}
            >
              {readOnly || slots.length === 0 ? (
                'Back to bracket'
              ) : complete ? (
                <>
                  Continue to winner picks{' '}
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                </>
              ) : (
                'Done for now'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!profile}
        onOpenChange={(value) => {
          if (!value) setProfile(null);
        }}
      >
        <DialogContent className="w-[calc(100vw-1rem)] max-w-xl max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{profile?.name}</DialogTitle>
            <DialogDescription>
              Public start.gg profile and tournament history.
            </DialogDescription>
          </DialogHeader>
          {profile && <CompetitorProfile competitor={profile} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}
